<template>
  <div class="mt-0 d-flex flex-column p-0 bg-transparent">
    <datasets-top-controls :facets="facets" :getPage="getPage" :getLimit="getLimit" class="datasets-top-controls"
      ref="sortComponent" @update-sort-type="handleSortType" />
    <div class="container-fluid datasets content">
      <div class="row">
        <dataservices-facets v-if="useDatasetFacets && showFilter"
          class="col-md-3 col-12 mb-3 mb-md-0 px-0 collapse d-none d-md-block" id="datasetFacets"
          :dataScope="dataScope" />
        <dataservices-content />
      </div>
      <div class="row" v-if="getSortType !== 'mostLiked'">
        <div class="column col-12 col-md-9 offset-md-3">
          <pagination class="mt-3" :items-count="getDatasetsCount" :items-per-page="getLimit" :get-page="getPage"
            :get-page-count="getPageCount" @setPageLimit="setPageLimit" :show-export="true" />
        </div>
      </div>
      <div class="row" v-else>
        <div class="column col-12 col-md-9 offset-md-3">
          <pagination class="mt-3" :items-count="getMostLikedTotal" :items-per-page="getLimit" :get-page="mostLikedPage"
            :get-page-count="Math.ceil(getMostLikedTotal / getLimit)" :manual-pagination-mode="true"
            @page-changed="handleMostLikedPageChange" @setPageLimit="setPageLimit" :show-export="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { debounce, has, isArray, isNil } from "lodash-es";
import $ from "jquery";
import {
  DatasetsTopControls,
  fileTypes
} from "@piveau/piveau-hub-ui-modules";
import DataservicesFacets from "./DataservicesFacets.vue";
import DataservicesContent from "./DataservicesContent";
import Pagination from "../Pagination";

export default {
  name: "Dataservices",
  components: {
    DataservicesContent,
    DatasetsTopControls,
    DataservicesFacets,
    Pagination,
  },
  props: {
    infiniteScrolling: {
      type: Boolean,
      default: false,
    },
  },
  metaInfo() {
    return {
      titleTemplate() {
        return this.$t("message.datasets.meta.page.title");
      },
      meta: [
        {
          name: "description",
          vmid: "description",
          content: `${this.$t("message.datasets.meta.description")}`,
        },
        {
          name: "keywords",
          vmid: "keywords",
          content: `${this.$env.keywords} ${this.$t(
            "message.datasets.meta.description"
          )}}`,
        },
        { name: "robots", vmid: "robots", content: "index, follow" },
      ],
    };
  },
  data() {
    return {
      debouncedOnBottomScroll: debounce(this.onBottomScroll, 500),
      facetFields: [],
      useDatasetFacets: this.$env.content.datasets.facets.useDatasetFacets,
      currentSortType: "",
    };
  },
  computed: {
    ...mapGetters("datasets", [
      "getDatasetsCount",
      "getFacets",
      "getLimit",
      "getSort",
      "getPage",
      "getQuery",
      "getPageCount",
      "getDatasets",
      "getMinScoring",
      "getMaxScoring",
      "search", ["getSortType"],
    ]),
    ...mapGetters("search", ["getSortType", 'getMostLikedTotal', 'getSortTriggerId', 'getShowFilter']),
    showCatalogDetails() {
      return !isNil(this.$route.params.ctlg_id);
    },
    showFilter() {
      return this.getShowFilter;
    },
    /**
     * @description Returns the current page.
     * @returns {Number}
     *
     * @deprecated use getPage from datasets store instead
     */
    page() {
      return this.$route.query.page;
    },
    /**
     * @description Returns the active facets.
     * @returns {Object}
     */


    facets() {
      const facets = {};
      for (const rawField of this.facetFields) {
        const field = rawField === "geospatial" ? "source_type" : rawField;
        if (Object.prototype.hasOwnProperty.call(facets, field)) continue;
        let urlFacets;
        if (field === "catalog" && !isNil(this.$route.params.ctlg_id))
          urlFacets = [this.$route.params.ctlg_id];
        else urlFacets = this.$route.query[field];
        if (!urlFacets) urlFacets = [];
        else if (!Array.isArray(urlFacets)) urlFacets = [urlFacets];
        facets[field] = urlFacets;
      }
      return facets;
    },
    currentSearchQuery() {
      return this.$route.query.query;
    },
    dataScope() {
      if (!this.$route.query.dataScope) return null;
      if (
        isArray(this.$route.query.dataScope) &&
        this.$route.query.dataScope.length > 0
      )
        return this.$route.query.dataScope[0];
      if (
        isArray(this.$route.query.dataScope) &&
        this.$route.query.dataScope.length === 0
      )
        return null;
      return this.$route.query.dataScope;
    },
    mostLikedPage() {
      return this.$store.state.search.mostLikedPage;
    }
  },
  methods: {
    isNil,
    ...mapActions("datasets", [
      "loadDatasets",
      "loadAdditionalDatasets",
      "setPage",
      "addFacet",
      "setFacets",
      "setFacetOperator",
      "setFacetGroupOperator",
      "setDataServices",
      "setPageCount",
      "setLimit",
      "setSort",
      "setDataScope",
      "setDateType",
      "setMaxScoring",
      "setAvailableFacets"
    ]),
    ...mapActions('search', ['setDataserviceItems', 'setDataserviceCount', 'setCombinedItems', 'setTotalCount', 'setSearchFacets']),
    initMaxScoring() {
      const maxScoring = parseInt(this.$route.query.maxScoring, 10);
      if (maxScoring > 0) this.setMaxScoring(maxScoring);
    },
    isArray,
    /**
     * @description Handler-function for the scroll event.
     */
    onScroll() {
      const items = this.$el.querySelectorAll(".dataset");
      const lastItem = items[items.length - 1];
      if (lastItem) {
        const lastItemPos = lastItem.getBoundingClientRect();
        if (lastItemPos.bottom - window.innerHeight <= 0) {
          this.debouncedOnBottomScroll();
        }
      }
    },
    /**
     * @description Handler-function when bottom of the page is reached.
     */
    onBottomScroll() {
      this.$nextTick(() => {
        this.$Progress.start();
        this.loadAdditionalDatasets()
          .then(() => {
            this.$Progress.finish();
          })
          .catch(() => {
            this.$Progress.fail();
          });
      });
    },
    initLimit() {
      const limit = parseInt(this.$route.query.limit, 10);
      if (limit > 0) this.setLimit(limit);
    },
    initSort() {
      const sort = this.$route.query.sort;
      if (sort) this.setSort(sort);
    },
    setPageLimit(value) {
      this.setLimit(value);
      if (this.getSortType === "mostLiked") {
        sessionStorage.setItem("mostLikedPage", 1);
        this.$store.commit("search/SET_MOST_LIKED_PAGE", 1);
        this.$store.commit("search/TRIGGER_SORT_RELOAD");
      } else {
        this.loadData();
      }
    },
    initDataScope() {
      this.setDataScope(this.dataScope);
    },
    /**
     * @description Determines the current page.
     */
    initPage() {
      const page = parseInt(this.$route.query.page, 10);
      if (page > 0) this.setPage(page);
      else this.setPage(1);
    },
    /**
     * @descritption Initialize the active facets by checking the route parameters
     */

    initFacets() {
      const fields = this.$env.content.datasets.facets.defaultFacetOrder;
      for (const rawField of fields) {
        const field = rawField === "geospatial" ? "source_type" : rawField;
        if (!this.facetFields.includes(field)) this.facetFields.push(field);
        if (field === "catalog" && !isNil(this.$route.params.ctlg_id)) {
          this.addFacet({ field, facet: this.$route.params.ctlg_id });
        } else if (
          !Object.prototype.hasOwnProperty.call(this.$route.query, [field])
        ) {
          this.$router
            .replace({
              query: Object.assign({}, this.$route.query, { [field]: [] }),
            })
            .catch((error) => {
              console.error(error);
            });
        } else {
          let routeFacets = this.$route.query[field];
          if (!Array.isArray(routeFacets)) routeFacets = [routeFacets];
          for (const facet of routeFacets) {
            // do not add duplicates!
            if (!this.getFacets[field]?.includes(facet)) {
              this.addFacet({ field, facet });
            }
          }
        }
      }
    },
    initFacetOperator() {
      // Always set facet operator to AND when in catalog details mode
      if (this.showCatalogDetails) this.setFacetOperator("AND");
      else {
        const op = this.$route.query.facetOperator;
        if (op === "AND" || op === "OR") this.setFacetOperator(op);
      }
    },
    initFacetGroupOperator() {
      // The facetGroupOperator should be the same as the facetOperator
      // Always set facet operator to AND when in catalog details mode
      if (this.showCatalogDetails) this.setFacetGroupOperator("AND");
      else {
        const op = this.$route.query.facetOperator;
        if (op === "AND" || op === "OR") this.setFacetGroupOperator(op);
      }
    },
    /**
     * @descritption Initialize the active data services facet by checking the route parameters
     */
    initDataServices() {
      const ds = this.$route.query.dataServices;
      if (ds === "true" || ds === "false") this.setDataServices(ds);
      else {
        this.setDataServices("false");
      }
    },
    initDatasets() {
      this.$nextTick(() => {
        this.loadDatasets({
          locale: this.$route.query.locale,
          fields: this.$route.query.fields,
          minDate: this.$route.query.minDate,
          maxDate: this.$route.query.maxDate,
          dateType: this.$route.query.dateType,
          maxScoring: this.$route.query.maxScoring
        })
          .then(() => {
            this.setPageCount(
              Math.ceil(this.getDatasetsCount / this.getLimit)
            );
            this.$Progress.finish();
            $('[data-toggle="tooltip"]').tooltip({
              container: "body",
            });
          })
          .catch(() => this.$Progress.fail())
          .finally(() => this.$root.$emit("contentLoaded"));
      });
    },
    initInfiniteScrolling() {
      if (this.infiniteScrolling)
        window.addEventListener("scroll", this.onScroll);
    },
    getFileTypeColor(format) {
      return fileTypes.getFileTypeColor(format);
    },
    handleSortType(sortType) {
      this.currentSortType = sortType;
    },
    handleMostLikedPageChange(page) {
      if (this.getSortType === "mostLiked") {
        sessionStorage.setItem("mostLikedPage", page);
        this.$store.commit("search/SET_MOST_LIKED_PAGE", page);
        this.$store.commit("search/TRIGGER_SORT_RELOAD");
      }
    },
    assembleNetworkingParams() {
      const activeFacetKeys = Object.keys(this.facets).filter(key => key !== "dataServices" && this.facets[key]?.length);
      const facetsObject = activeFacetKeys.reduce((acc, key) => ({ ...acc, [key]: this.facets[key] }), {});
      const params = {
        filters: "dataservice"
      };
      if (this.getQuery) params.q = this.getQuery;
      if (this.getLimit) params.limit = this.getLimit;
      if (this.getPage) params.page = this.getPage - 1;
      if (this.getSort) params.sort = this.getSort;
      if (this.getDataServices) params.dataServices = this.getDataServices;
      if (this.getDataScope) {
        const dataScope = this.getDataScope;
        params.countryData = dataScope === 'countryData';
        if (params.countryData) {
          if (facetsObject.country) {
            facetsObject.country = facetsObject.country.filter(c => c !== 'countryData');
            if (facetsObject.country.length === 0) {
              delete facetsObject.country;
            }
          }
        } else {
          facetsObject.country = [dataScope];
        }
      }

      if (this.getMinScoring > 0) params.minScoring = this.getMinScoring;
      let bounds = this.getDatasetGeoBounds;
      if (bounds instanceof Array && bounds[0] && bounds[1]) {
        bounds = [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]]
        if (bounds.every(bound => parseFloat(bound))) {
          params.bboxMinLat = bounds[0];
          params.bboxMaxLat = bounds[2];
          params.bboxMinLon = bounds[1];
          params.bboxMaxLon = bounds[3];
        }
      }
      if (Object.keys(facetsObject).length) {
        params.facets = JSON.stringify(facetsObject);
      }
      return params;
    },
    transformFetchedFacets(facets) {
      const transformed = [];
      for (const field of facets) {
        // Check for required field keys
        if (has(field, 'id') && has(field, 'title') && has(field, 'items')) {
          const items = [];
          for (const facet of field.items) {
            const item = {};
            // Check for required facet/item keys
            if (has(facet, 'id') && has(facet, 'title') && has(facet, 'count')) {
              item.id = facet.id;
              item.title = facet.title;
              item.count = facet.count;
            }
            // Handle Scoring Facets
            if (has(facet, 'from') && has(facet, 'to')) {
              const currentScoringFacet = this.defaultScoringFacets[facet.id];
              item.minScoring = facet.from;
              item.maxScoring = facet.to;

              // Use config values to overwrite the default values from the backend
              if (currentScoringFacet.title) item.title = currentScoringFacet.title;
              if (currentScoringFacet.minScoring) item.minScoring = currentScoringFacet.minScoring;
              if (currentScoringFacet.maxScoring) item.maxScoring = currentScoringFacet.maxScoring;
            }
            items.push(item);
          }
          // Add to response array
          transformed.push({
            id: field.id,
            title: field.title,
            items,
          });
        }
      }
      return transformed;
    },
    async loadData() {
      this.$Progress.start();
      // Set loading state for datasets store to show "loading" message in SearchCount
      this.$store.dispatch('datasets/setLoading', true);
      const baseUrl = this.$env.api.baseUrl;
      const endpoint = "search";
      const queryParams = new URLSearchParams(this.assembleNetworkingParams());
      const requestString = `${baseUrl}${endpoint}?${queryParams}`;
      try {
        const response = await fetch(requestString);
        const data = await response.json();

        const count = data.result.count;
        this.setDataserviceCount(count);

        this.setAvailableFacets(this.transformFetchedFacets(data.result.facets));

        const dataserviceResult = data.result.results;
        this.setDataserviceItems(dataserviceResult);

        const facets = data.result.facets;
        this.setSearchFacets(facets);

        // const totalCount = data.result.reduce((acc, curr) => +(curr.count) + acc, 0);
        this.setPageCount(
          Math.ceil(count / this.getLimit)
        );

        this.$Progress.finish();

        this.$nextTick(() => {
          $('[data-toggle="tooltip"]').tooltip({
            container: "body",
          });
        });
      } catch (error) {
        this.$Progress.fail();
        console.error(error)
      } finally {
        // Always reset loading state
        this.$store.dispatch('datasets/setLoading', false);
        console.log("Data loading finished");
      }
      this.$root.$emit('contentLoaded');
    }
  },
  watch: {
    /**
     * @description Watcher for active facets
     */
    // eslint-disable-next-line object-shorthand
    facets: {
      handler(facets) {
        this.setFacets(facets);
      },
      deep: true,
    },
    // eslint-disable-next-line object-shorthand
    page(pageStr) {
      const page = parseInt(pageStr, 10);
      if (page > 0) this.setPage(page);
      else this.setPage(1);
    },
    dataScope: {
      handler() {
        this.initDataScope();
      },
      deep: true,
    },
    // Watch for changes in route query parameters that affect search results
    '$route.query': {
      handler(newQuery, oldQuery) {
        // Check if relevant search parameters changed
        const relevantParams = ['fields', 'query', 'minDate', 'maxDate', 'dateType', 'maxScoring'];
        const hasRelevantChange = relevantParams.some(param =>
          newQuery[param] !== oldQuery[param]
        );

        if (hasRelevantChange) {
          // Reload datasets when search-affecting parameters change
          this.$nextTick(() => {
            this.loadData();
          });
        }
      },
      deep: true
    }
  },
  created() {
    this.$Progress.start();
    if (this.$route.query.clear === "true") {
      this.$nextTick(() => {
        this.$router.push('/datasets/advanced');
      });
    }
    this.initDataScope();
    this.initLimit();
    this.initPage();
    this.initSort();
    this.initFacetOperator();
    this.initMaxScoring();
    this.initFacetGroupOperator();
    this.initDataServices();
    this.initFacets();
    this.$nextTick(() => {
      this.loadData();
    });
    this.initInfiniteScrolling();
    setTimeout(() => {
      $(".facets-show-more").each(function () {
        $(this).click(function () {
          $("body").tooltip({ selector: '[data-toggle="tooltip"]' });
        });
      });
    }, 1500);
  },
  mounted() {
    // This is supposed to fix the browser issue (https://gitlab.fokus.fraunhofer.de/piveau/organisation/piveau-scrum-board/-/issues/2344)
    if (this.$route.query.refresh === "true") {
      this.$nextTick(() => {
        this.loadData();
      });
    }
  },
  beforeDestroy() {
    $(".tooltip").remove();
    if (this.infiniteScrolling)
      window.removeEventListener("scroll", this.onScroll);
  },
};
</script>

<style lang="scss" scoped>
.dataset-info-box:hover {
  box-shadow: none !important;
  cursor: pointer;
}

#erdpInfobox {
  display: flex;
  padding: 1.25rem;

  .infoIcon {
    height: 20%;
    width: 60%;
    fill: #175baf;
  }

  border: 2px solid #175baf;
}

.ecl-message.ecl-message--info {
  //border-left: 4px solid #0xe47cb !important;
  border-radius: 4px 0 0 4px;
  border: none;
  background-color: #f3f6fc;
}

.alert-primary {
  color: #042648;
  background-color: #cddbe8;
  border-color: #baccdf;
}

.content {
  padding: 30px 30px 0 30px;
  // margin-top: 15px;
  margin-bottom: 15px;
  background-color: white;
}

.router-link-active {
  color: #175baf !important;
  border: none !important;
  border-bottom: 2px solid #175baf !important;
}

.router-link-inactive {
  color: rgba(0, 0, 0, 0.7);
  border: none !important;

  &:hover {
    color: #175baf;
  }
}

.material-icons.small-icon {
  font-size: 20px;
}

@media screen and (min-width: 768px) {
  #erdpInfobox {
    margin-left: 15px;
  }

  #datasetFacets {
    display: block;
  }
}

@media screen and (max-width: 767px) {

  .datasets {
    padding-top: 5px;
  }

}
</style>
